import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  MessageSquare,
  Send,
  X,
  Sparkles,
  User,
  Loader2,
  CheckCheck,
  Building2,
  PhoneCall,
  BedDouble,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useGuestChatSession } from "@/hooks/chat/useGuestChatSession.js";
import { cn } from "@/lib/utils";

const QUICK_GUEST_CHIPS = [
  { label: "🛎️ Extra Towels", text: "Hello! Could we please have extra towels sent to our room?" },
  { label: "🧹 Clean Room", text: "Hello! Please arrange housekeeping for our room when convenient." },
  { label: "📶 WiFi Details", text: "Hello! Could you please share the high-speed WiFi login info?" },
  { label: "⏰ Late Checkout", text: "Hello! Could we request a late checkout tomorrow if available?" },
  { label: "🍽️ Dining Menu", text: "Hello! Could you please send today's room dining menu?" },
  { label: "💧 Water Bottles", text: "Hello! Could we please have extra drinking water bottles delivered?" },
];

const formatChatTime = (dateVal) => {
  if (!dateVal) return "";
  let d;
  if (typeof dateVal === "string") {
    if (!dateVal.endsWith("Z") && !dateVal.includes("+") && !/-\d{2}:\d{2}$/.test(dateVal)) {
      d = new Date(dateVal + "Z");
    } else {
      d = new Date(dateVal);
    }
  } else if (dateVal instanceof Date) {
    d = dateVal;
  } else {
    d = new Date(dateVal);
  }
  if (isNaN(d.getTime())) return "";
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

const GuestChatDrawer = ({ isOpen, onClose, guestInfo }) => {
  const {
    messages,
    loading,
    sending,
    otherUserTyping,
    sendMessage,
    sendTyping,
    loadInitialData,
  } = useGuestChatSession({
    guestInfo,
    defaultDepartment: "Reception",
  });

  const [inputVal, setInputVal] = useState("");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      loadInitialData();
    }
  }, [isOpen, loadInitialData]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (isOpen) {
      scrollToBottom();
    }
  }, [messages, isOpen]);

  const handleSend = async (e) => {
    e?.preventDefault();
    if (!inputVal.trim() || sending) return;
    const text = inputVal;
    setInputVal("");
    sendTyping(false);
    await sendMessage({ content: text });
  };

  const handleInputChange = (e) => {
    setInputVal(e.target.value);
    sendTyping(e.target.value.length > 0);
  };

  const handleChipClick = async (chip) => {
    await sendMessage({ content: chip.text });
  };

  if (!isOpen) return null;

  const drawerContent = (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-stretch justify-center sm:justify-end bg-slate-900/60 backdrop-blur-xs transition-opacity duration-200 animate-in fade-in">
      {/* Solid Backdrop click to close */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Solid Opaque Chat Popup / Sheet */}
      <div className="relative z-10 flex h-[90dvh] sm:h-full w-full max-w-lg flex-col rounded-t-3xl sm:rounded-none sm:rounded-l-3xl border-t sm:border-t-0 sm:border-l border-slate-200 bg-white text-slate-900 shadow-2xl transition-transform duration-300 animate-in slide-in-from-bottom sm:slide-in-from-right overflow-hidden">
        {/* Header - 100% Solid Clean Styling */}
        <div className="flex items-center justify-between border-b border-slate-100 bg-slate-900 px-4 py-3.5 text-white sm:px-5">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-amber-500/20 text-amber-400 ring-1 ring-amber-400/30">
              <Building2 className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-bold tracking-tight text-white sm:text-base truncate">
                  Reception Concierge
                </h2>
                {guestInfo?.roomName && (
                  <span className="rounded-md bg-white/15 px-1.5 py-0.5 text-[10px] font-bold text-amber-300">
                    Room {guestInfo.roomName}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1.5 text-xs text-emerald-400">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                </span>
                <span>Active 24/7 Front Desk Team</span>
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close Chat"
            className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/10 text-slate-300 hover:bg-white/20 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Quick Suggestion Chips */}
        <div className="border-b border-slate-100 bg-slate-50/90 px-3.5 py-2.5">
          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-0.5">
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 shrink-0">
              Quick:
            </span>
            {QUICK_GUEST_CHIPS.map((chip, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => handleChipClick(chip)}
                disabled={sending}
                className="inline-flex items-center rounded-xl border border-slate-200/90 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 shadow-2xs transition-all hover:border-amber-500 hover:bg-amber-50 hover:text-amber-900 active:scale-95 disabled:opacity-50 shrink-0"
              >
                {chip.label}
              </button>
            ))}
          </div>
        </div>

        {/* Messages List Area - Clean High Contrast Solid Background */}
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3.5 bg-slate-50/50">
          {loading && messages.length === 0 ? (
            <div className="flex h-full items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-slate-900" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center p-6 text-slate-500">
              <div className="flex h-14 w-14 items-center justify-center rounded-3xl bg-amber-50 text-amber-600 mb-3 shadow-sm">
                <MessageSquare className="h-7 w-7" />
              </div>
              <h3 className="text-sm sm:text-base font-bold text-slate-900">Direct Concierge Message</h3>
              <p className="mt-1 text-xs text-slate-500 max-w-[260px] leading-relaxed">
                Need extra amenities, room cleaning, or have any question? Type a message or select a quick option above.
              </p>
            </div>
          ) : (
            (Array.isArray(messages) ? messages : []).map((msg, index) => {
              const isMe = msg.senderType === "Guest";
              const time = formatChatTime(msg.createdAt);

              return (
                <div
                  key={msg.id || index}
                  className={cn("flex flex-col", isMe ? "items-end" : "items-start")}
                >
                  <div className="flex items-end gap-2 max-w-[85%]">
                    {!isMe && (
                      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-xs font-bold text-white shadow-2xs">
                        {msg.senderDisplayName ? msg.senderDisplayName[0] : "S"}
                      </div>
                    )}

                    <div
                      className={cn(
                        "rounded-2xl px-4 py-2.5 text-xs sm:text-sm shadow-xs",
                        isMe
                          ? "bg-slate-900 text-white rounded-br-none"
                          : "bg-white text-slate-800 border border-slate-200/90 rounded-bl-none"
                      )}
                    >
                      {!isMe && (
                        <p className="text-[10px] font-bold text-amber-600 mb-0.5">
                          {msg.senderDisplayName || "Hotel Front Desk"}
                        </p>
                      )}
                      <p className="whitespace-pre-wrap leading-relaxed">{msg.content}</p>
                      <div
                        className={cn(
                          "mt-1 flex items-center justify-end gap-1 text-[9px]",
                          isMe ? "text-slate-400" : "text-slate-400"
                        )}
                      >
                        <span>{time}</span>
                        {isMe && (
                          <CheckCheck
                            className={cn("h-3 w-3", msg.isRead ? "text-sky-400" : "text-slate-400")}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}

          {otherUserTyping && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500 italic">
              <span className="h-2 w-2 rounded-full bg-amber-500 animate-ping" />
              <span>{otherUserTyping} is typing...</span>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Footer - Solid Clean White */}
        <form
          onSubmit={handleSend}
          className="border-t border-slate-200 bg-white p-3 sm:p-4 shadow-lg"
        >
          <div className="flex items-center gap-2">
            <Input
              type="text"
              placeholder="Type your request or message..."
              value={inputVal}
              onChange={handleInputChange}
              className="flex-1 rounded-xl border-slate-200 bg-slate-50 px-4 py-2.5 text-xs sm:text-sm text-slate-900 placeholder:text-slate-400 focus-visible:ring-slate-900 focus-visible:bg-white"
            />
            <Button
              type="submit"
              size="sm"
              disabled={!inputVal.trim() || sending}
              className="h-10 w-10 shrink-0 rounded-xl bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50 transition-all shadow-xs"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(drawerContent, document.body) : drawerContent;
};

export default GuestChatDrawer;
